import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Activity, ArrowDown, Check, Mic, RotateCcw, X } from "lucide-react";
import DxClause from "../foundational/DxClause";
import BenitoAvatar from "../BenitoAvatar";
import PresenciaBenito from "../PresenciaBenito";
import { CIRCULO_TURNO } from "../calendario/TiraSemana";
import { EJEMPLOS } from "../../lib/presentacionMiPAE";
import { colorModulo } from "./colorModulo";
import { retraso } from "./useAparecerAlBajar";

// Viñetas del producto para la presentación pública. Son fragmentos de
// pantallas REALES de MiPAE redibujados con los mismos tokens y componentes
// (DxClause, BenitoAvatar, PresenciaBenito, los círculos del calendario),
// no capturas: así siguen al tema y al modo oscuro, pesan casi nada y no
// arrastran datos de nadie. Todas llevan "Ejemplo ficticio" a la vista.
//
// Movimiento (29-sep, "más acción"): el contenido de cada viñeta entra en
// cascada con .pp-entra, en el orden en que pasa en la app. Arranca al
// montarse (cambiar de pestaña lo repite) y, si la viñeta aún no llega a la
// pantalla, espera quieta en su primer cuadro (index.css).
//
// Los controles que NO hacen nada son <span>, no <button>: un botón de
// mentira confunde al lector de pantalla y al dedo. La única viñeta que se
// puede tocar es la pregunta de Modo estudio, y ahí la respuesta es real.

// Rótulos internos en frase normal (pasada "menos Claude"): menos
// mayúsculas espaciadas, la misma información.
const ETIQUETA = "text-small font-semibold text-ink-subtle dark:text-ink-muted";

export function Vineta({ modulo, herramienta, pie, children, className = "", compacta = false }) {
  const c = colorModulo(modulo);
  return (
    <figure className={`relative overflow-hidden rounded-tk-lg border border-edge bg-paper shadow-tk-sm sm:rounded-[20px] lg:shadow-tk-modal ${className}`}>
      <div aria-hidden="true" className={`h-[3px] ${c.punto}`} />
      <div className="flex items-center gap-2 border-b border-edge px-4 py-2.5">
        <span aria-hidden="true" className={`h-2 w-2 flex-shrink-0 rounded-full ${c.punto}`} />
        <span className="min-w-0 text-small font-semibold text-ink">{herramienta}</span>
        <span className="ml-auto flex-shrink-0 whitespace-nowrap rounded-full border border-edge bg-surface px-2 py-0.5 text-micro text-ink-subtle dark:text-ink-muted">
          Ejemplo ficticio
        </span>
      </div>
      <div className={compacta ? "p-3.5" : "p-4 sm:p-5"}>{children}</div>
      {pie && !compacta && (
        <figcaption className="border-t border-edge bg-surface px-4 py-3 text-small font-normal leading-relaxed text-ink-subtle dark:text-ink-muted">
          {pie}
        </figcaption>
      )}
    </figure>
  );
}

// ─── Aprender · Modo estudio ─────────────────────────────────────────────────
// Misma pregunta, mismas opciones y mismo lenguaje de acierto que
// referencia/SeccionEstudio.jsx (tokens system-*: es un quiz, no una señal
// clínica). La explicación es la lectura del intérprete de gases.
export function VinetaEstudio() {
  const e = EJEMPLOS.estudio;
  const [sel, setSel] = useState(null);
  const idPregunta = useId();
  const primeraOpcion = useRef(null);
  // aria-disabled y no disabled: un botón deshabilitado suelta el foco y el
  // teclado o el lector de pantalla quedan en el <body> (revisión 29-sep).
  const reintentar = () => { setSel(null); primeraOpcion.current?.focus(); };
  const acierto = sel === e.correcta;
  return (
    <Vineta
      modulo="enciclopedia"
      herramienta="Modo estudio"
      pie="Pruébalo aquí. En la app hay preguntas de gases, laboratorio, aislamiento, lesiones por presión, escalas, cuidados del paciente y GES."
    >
      <p className={ETIQUETA}>Gases arteriales</p>
      <p id={idPregunta} className="mt-1.5 text-body font-semibold text-ink">{e.enunciado}</p>
      <div role="group" aria-labelledby={idPregunta} className="mt-3 grid gap-2">
        {e.opciones.map((op) => {
          const esCorrecta = op === e.correcta;
          const elegida = op === sel;
          let cls = "border-edge bg-paper text-ink hover:border-accent";
          let Icono = null;
          if (sel) {
            if (esCorrecta) { cls = "border-system-success-border bg-system-success-bg text-ink"; Icono = Check; }
            else if (elegida) { cls = "border-system-error-border bg-system-error-bg text-ink"; Icono = X; }
            else cls = "border-edge bg-paper text-ink-subtle dark:text-ink-muted";
          }
          return (
            <button
              key={op}
              ref={op === e.opciones[0] ? primeraOpcion : undefined}
              type="button"
              aria-disabled={!!sel}
              aria-pressed={elegida}
              onClick={() => { if (!sel) setSel(op); }}
              style={retraso(120 + e.opciones.indexOf(op) * 70)}
              className={`pp-entra flex min-h-[44px] w-full items-center justify-between gap-2 rounded-tk-md border px-3 py-2 text-left text-small font-sans transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${sel ? "cursor-default" : ""} ${cls}`}
            >
              <span>{op}</span>
              {Icono && <Icono aria-hidden="true" size={16} strokeWidth={2.4} className={esCorrecta ? "text-system-success" : "text-system-error"} />}
            </button>
          );
        })}
      </div>
      <div aria-live="polite">
        {sel && (
          <div className="pp-entra mt-3 rounded-tk-md border border-edge bg-surface p-3">
            <p className={`text-small font-semibold ${acierto ? "text-system-success" : "text-system-error"}`}>
              {acierto ? "¡Correcto!" : `Era: ${e.correcta}`}
            </p>
            <p className="mt-1 text-small font-normal leading-relaxed text-ink-subtle dark:text-ink-muted">{e.porque}</p>
            <p className="mt-2 text-small font-normal text-ink-subtle dark:text-ink-muted">
              <span className="font-semibold text-ink">Lectura de la Enciclopedia.</span> {e.lectura}
            </p>
            <button
              type="button"
              onClick={reintentar}
              className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 rounded-tk-sm text-small font-semibold text-accent dark:text-accent-light hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            >
              <RotateCcw aria-hidden="true" size={14} strokeWidth={2} />
              Intentar de nuevo
            </button>
          </div>
        )}
      </div>
    </Vineta>
  );
}

// ─── Fundamentar · PAE Rápido ────────────────────────────────────────────────
// La ficha de diagnóstico de PAE Rápido (DiagnosticArticle + DxClause). El
// título va en <p> y no en <h2> como en la app: aquí es una muestra dentro de
// una página que ya tiene sus propios títulos.
// `compacta`: la versión del aviso del inicio (membresias/AvisoLoNuevo.jsx).
// Sin el caso ni el plan: el diagnóstico con lo que lo manifiesta y su
// razonamiento, que es lo que se quiere mostrar.
export function VinetaPae({ compacta = false }) {
  const p = EJEMPLOS.pae;
  return (
    <Vineta
      modulo="pae-rapido"
      herramienta="PAE Rápido"
      compacta={compacta}
      pie="Es el caso de demostración de la app. Sirve para aprender y no es una indicación para un paciente real."
    >
      <p className={ETIQUETA}>Caso ficticio · {p.contexto}</p>
      {!compacta && <p className="mt-1.5 text-small font-normal leading-relaxed text-ink-subtle dark:text-ink-muted">{p.caso}</p>}
      <div className={`pp-entra border-t-2 border-t-priority ${compacta ? "mt-2.5 pt-3" : "mt-4 pt-4"}`} style={retraso(150)}>
        <p className="flex items-baseline gap-2">
          <span aria-hidden="true" className="font-serif text-22px font-semibold leading-none text-ink-muted">01</span>
          <span className="text-micro uppercase tracking-[0.14em] text-ink">Diagnóstico prioritario</span>
        </p>
        <p className="mt-2 font-serif text-22px font-semibold leading-tight text-ink">{p.diagnostico}</p>
        <p className="mt-1 font-mono text-10.5px text-ink-subtle dark:text-ink-muted">NANDA · {p.codigo}</p>
        <div className={`pp-entra space-y-4 ${compacta ? "mt-3" : "mt-4"}`} style={retraso(450)}>
          <DxClause label="Manifestado por" tone="stable">{p.manifestadoPor}</DxClause>
          {!compacta && <DxClause label="Relacionado con" tone="attention">{p.relacionadoCon}</DxClause>}
          <DxClause label="Razonamiento clínico" tone="brand" variant="pull-quote">{p.razonamiento}</DxClause>
        </div>
        {!compacta && <ul className="mt-4 flex flex-wrap gap-2" aria-label="Plan de cuidados de referencia">
          {[`NOC ${p.noc.codigo} · ${p.noc.nombre}`, `NIC ${p.nic.codigo} · ${p.nic.nombre}`].map((t, i) => (
            <li key={t} style={retraso(800 + i * 120)} className="pp-pop rounded-full border border-edge bg-surface px-2.5 py-1 text-11px font-medium text-ink-subtle dark:text-ink-muted">{t}</li>
          ))}
        </ul>}
      </div>
    </Vineta>
  );
}

// ─── Organizar · MiPAE en turno ──────────────────────────────────────────────
// De la cronología (con hora, es registro) a la evolución por sistema (sin
// hora: la prosa generada no la imprime). `conSemana` suma la tira del
// calendario de turnos, con los mismos círculos de TiraSemana.
export function VinetaTurno({ conSemana = false }) {
  const t = EJEMPLOS.turno;
  const c = colorModulo("prepara-turno");
  const iActual = t.fases.indexOf(t.faseActual);
  return (
    <Vineta
      modulo="prepara-turno"
      herramienta="MiPAE en turno"
      pie="La evolución se organiza a partir de lo registrado y la revisa quien registra."
    >
      <p className={ETIQUETA}>Paciente ficticio · {t.contexto}</p>
      {/* En un teléfono de 390 px o menos, "Evolucionar" no cabe en un
          quinto del ancho y se cortaba justo la fase resaltada (revisión
          29-sep). Ahí van las cinco barras y, abajo, la fase actual entera;
          con ancho, los cinco nombres. */}
      <ol aria-label="Fases del turno" className="mt-3 grid grid-cols-5 gap-1.5">
        {t.fases.map((f, i) => (
          <li key={f} className="flex min-w-0 flex-col gap-1.5" aria-current={i === iActual ? "step" : undefined}>
            <span aria-hidden="true" style={retraso(i * 110)} className={`h-1.5 rounded-full ${i <= iActual ? `pp-pop ${c.punto}` : "bg-edge"}`} />
            <span className={`hidden truncate text-10px sm:block ${i === iActual ? "font-semibold text-ink" : "text-ink-subtle dark:text-ink-muted"}`}>{f}</span>
            <span className="sr-only sm:hidden">{f}</span>
          </li>
        ))}
      </ol>
      <p aria-hidden="true" className="mt-1.5 text-small sm:hidden">
        <span className="font-semibold text-ink">{t.faseActual}</span>
        <span className="text-ink-subtle dark:text-ink-muted"> · fase {iActual + 1} de {t.fases.length}</span>
      </p>

      <p className={`${ETIQUETA} mt-5`}>Durante · Cronología</p>
      <ol className="mt-2 space-y-2.5 border-l border-edge pl-4">
        {t.cronologia.map((e, i) => (
          <li key={e.hora} style={retraso(450 + i * 380)} className="pp-aterriza relative -mx-1.5 rounded-tk-sm px-1.5 py-0.5 text-small font-normal leading-snug text-ink">
            <span aria-hidden="true" className={`absolute -left-[15px] top-2 h-2 w-2 rounded-full ring-2 ring-paper ${c.punto}`} />
            <span className="mr-2 font-mono tabular-nums text-ink-subtle dark:text-ink-muted">{e.hora}</span>
            {e.texto}
          </li>
        ))}
      </ol>

      <p style={retraso(450 + 4 * 380)} className="pp-entra my-3 flex items-center gap-1.5 text-small font-medium text-ink-subtle dark:text-ink-muted">
        <ArrowDown aria-hidden="true" size={14} strokeWidth={2} />
        Se organiza por sistema
      </p>

      <div className="rounded-tk-md border border-edge bg-surface p-3.5">
        <p className={ETIQUETA}>Evolución</p>
        <dl className="mt-2 space-y-2.5">
          {t.evolucion.map((s, i) => (
            <div key={s.sistema} style={retraso(450 + 4 * 380 + 250 + i * 180)} className="pp-entra">
              <dt className="text-small font-semibold text-ink">{s.sistema}</dt>
              <dd className="text-small font-normal leading-relaxed text-ink-subtle dark:text-ink-muted">{s.texto}</dd>
            </div>
          ))}
        </dl>
      </div>

      {conSemana && (
        <div className="mt-4 border-t border-edge pt-4">
          <div className="flex items-baseline justify-between gap-3">
            <p className={ETIQUETA}>Calendario de turnos</p>
            <p className="flex items-center gap-3 text-11px text-ink-subtle dark:text-ink-muted">
              <span className="inline-flex items-center gap-1"><span aria-hidden="true" className={`h-2 w-2 rounded-full ${CIRCULO_TURNO.largo}`} />Largo</span>
              <span className="inline-flex items-center gap-1"><span aria-hidden="true" className={`h-2 w-2 rounded-full ${CIRCULO_TURNO.noche}`} />Noche</span>
            </p>
          </div>
          <ol className="mt-2 grid grid-cols-7 gap-1 text-center" aria-label="Semana de ejemplo">
            {t.semana.map((d) => (
              <li key={d.fecha} className="flex flex-col items-center gap-1">
                <span className="text-10px font-medium text-ink-subtle dark:text-ink-muted">{d.dia}</span>
                <span
                  className={`grid h-8 w-8 place-items-center rounded-full text-small font-semibold tabular-nums ${d.turno ? CIRCULO_TURNO[d.turno] : "text-ink-subtle dark:text-ink-muted"}`}
                  aria-label={`${d.fecha}, ${d.turno === "largo" ? "turno largo" : d.turno === "noche" ? "turno de noche" : "libre"}`}
                >
                  {d.fecha}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Vineta>
  );
}

// ─── Dictar y revisar · la evolución de MiPAE en turno ──────────────────────
// La invitación de MiPAE Enfermero (20-oct): lo que se dicta, cómo queda por
// sistemas y que se revisa antes de copiar. Mismo paciente ficticio de
// VinetaTurno. "Revisar" y "Copiar" son <span>: en la muestra no hacen nada.
// `compacta`: la versión del aviso del inicio. El dictado en dos líneas y dos
// sistemas, para que la transformación entre entera en la tarjeta.
export function VinetaEvolucion({ compacta = false }) {
  const d = EJEMPLOS.dictado;
  const t = EJEMPLOS.turno;
  const c = colorModulo("prepara-turno");
  const sistemas = compacta ? t.evolucion.slice(0, 2) : t.evolucion;
  return (
    <Vineta
      modulo="prepara-turno"
      herramienta="MiPAE en turno"
      compacta={compacta}
      pie="La evolución se organiza a partir de lo que registraste. La revisas y la corriges antes de copiarla."
    >
      <p className={ETIQUETA}>Paciente ficticio · {d.contexto}</p>
      <div className="pp-entra mt-2 flex items-start gap-2.5 rounded-tk-md border border-edge bg-surface p-3">
        <span aria-hidden="true" className={`mt-0.5 grid h-7 w-7 flex-shrink-0 place-items-center rounded-full ${c.tinte} ${c.icono}`}>
          <Mic size={14} strokeWidth={2} />
        </span>
        <p className={`min-w-0 text-small font-normal leading-relaxed text-ink ${compacta ? "line-clamp-2" : ""}`}>
          <span className="sr-only">Dictado: </span>{d.texto}
        </p>
      </div>
      <p style={retraso(500)} className={`pp-entra flex items-center gap-1.5 text-small font-medium text-ink-subtle dark:text-ink-muted ${compacta ? "my-2" : "my-3"}`}>
        <ArrowDown aria-hidden="true" size={14} strokeWidth={2} />
        Se organiza por sistema
      </p>
      <div className="rounded-tk-md border border-edge bg-paper p-3.5">
        <p className={ETIQUETA}>Tu evolución, para revisar</p>
        <dl className="mt-2 space-y-2.5">
          {sistemas.map((s, i) => (
            <div key={s.sistema} style={retraso(700 + i * 180)} className="pp-entra">
              <dt className="text-small font-semibold text-ink">{s.sistema}</dt>
              <dd className="text-small font-normal leading-relaxed text-ink-subtle dark:text-ink-muted">{s.texto}</dd>
            </div>
          ))}
        </dl>
        <div aria-hidden="true" style={retraso(1300)} className="pp-entra mt-3 flex flex-wrap gap-2 border-t border-edge pt-3">
          <span className="inline-flex min-h-[36px] items-center rounded-tk-sm border border-edge bg-paper px-3 text-small font-semibold text-ink">Revisar</span>
          <span className="inline-flex min-h-[36px] items-center rounded-tk-sm border border-edge bg-paper px-3 text-small font-semibold text-ink">Copiar</span>
        </div>
      </div>
    </Vineta>
  );
}

// ─── Registrar de un toque · MiPAE TENS ─────────────────────────────────────
// La hoja de registro rápido del TENS (tens/HojaCuidadoRapido.jsx): sin entrar
// a la ficha, el cambio de posición y los cuidados del turno quedan con su
// hora. Los chips son <span>: en la muestra no registran nada.
export function VinetaTens({ compacta = false }) {
  const t = EJEMPLOS.tens;
  return (
    <Vineta
      modulo="prepara-turno"
      herramienta="MiPAE TENS"
      compacta={compacta}
      pie="Lo que registras se escribe en tu evolución del turno, que revisas antes de copiar."
    >
      <p className={ETIQUETA}>Paciente ficticio · {t.contexto}</p>
      <div className="mt-2 rounded-tk-md border border-accent-border bg-surface p-3.5">
        <p className="text-small font-semibold text-ink">Registrar en la cama {t.cama}</p>
        <p className="mt-3 text-small font-medium text-ink-subtle dark:text-ink-muted">Cambio de posición</p>
        <span style={retraso(250)} className="pp-pop mt-1.5 inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-accent bg-accent-bg px-3 text-small font-semibold text-accent dark:text-accent-light">
          <Check aria-hidden="true" size={14} strokeWidth={2.4} />
          {t.posicion}
        </span>
        <p className="mt-3 text-small font-medium text-ink-subtle dark:text-ink-muted">Cuidados del turno</p>
        <ul className="mt-1.5 flex flex-wrap gap-1.5">
          {t.cuidados.map((cu, i) => (
            <li
              key={cu.label}
              style={retraso(450 + i * 150)}
              className={`pp-pop inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-small ${cu.hora ? "border-accent bg-accent-bg font-semibold text-accent dark:text-accent-light" : "border-edge bg-paper text-ink"}`}
            >
              {cu.hora && <Check aria-hidden="true" size={14} strokeWidth={2.4} />}
              {cu.label}
              {cu.hora && <span className="font-normal tabular-nums text-ink-subtle dark:text-ink-muted">{cu.hora}</span>}
            </li>
          ))}
        </ul>
      </div>
    </Vineta>
  );
}

// Se enciende cuando el elemento entra a la pantalla. Sin IntersectionObserver
// o con reduced-motion responde "visto" de entrada: el valor final, quieto.
function useVisto(ref) {
  const [animar] = useState(() => puedeAnimar());
  const [visto, setVisto] = useState(!animar);
  useEffect(() => {
    if (visto || !ref.current) return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisto(true); obs.disconnect(); }
    }, { threshold: 0.5 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [visto, ref]);
  return { animar, visto };
}

// Número que sube hasta su valor al aparecer (curva que frena al final).
// Solo presentación: el valor lo pone EJEMPLOS, que está fijado por test
// contra la función real de la app.
function useContador(hasta, desde, { animar, visto }, ms = 1100) {
  const [n, setN] = useState(animar ? desde : hasta);
  useEffect(() => {
    if (!animar || !visto) return undefined;
    let raf;
    const t0 = performance.now();
    const paso = (t) => {
      const p = Math.min(1, (t - t0) / ms);
      setN(Math.round(desde + (hasta - desde) * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(paso);
    };
    raf = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(raf);
  }, [animar, visto, desde, hasta, ms]);
  return n;
}

// ─── Consultar · Escalas, calculadoras y fichas ──────────────────────────────
// El resultado de la escala habla con la FORMA del contrato clínico (borde
// lateral grueso con el token clinical-*, igual que escalas/Escalas.jsx) y
// la calculadora con el mismo texto que CalcCard.
// Con movimiento (29-sep, el fundador pidió "más animación, por ejemplo la
// escala"): el puntaje sube desde el mínimo de la escala, la barra se llena
// hasta su lugar en 6 a 23 y recién ahí aparece la clasificación; el goteo
// sube a su valor y la gota cae al ritmo real del cálculo (60 / gotas por
// minuto segundos). El lector de pantalla lee siempre el valor final.
export function VinetaConsulta() {
  const b = EJEMPLOS.braden;
  const g = EJEMPLOS.goteo;
  const ref = useRef(null);
  const vista = useVisto(ref);
  const puntaje = useContador(b.total, 6, vista);
  const mlHora = useContador(g.mlHora, 0, vista, 1300);
  const clasificado = puntaje === b.total;
  return (
    <Vineta
      modulo="escalas"
      herramienta="Escalas, calculadoras y fichas"
      pie="Los puntajes y los cálculos salen de fórmulas. La decisión clínica sigue siendo tuya."
    >
      <div ref={ref} className="grid gap-3 sm:grid-cols-2">
        <div className="pp-entra rounded-tk-md border border-edge border-l-4 border-l-clinical-attention bg-paper p-4">
          <p className={ETIQUETA}>Escala de Braden</p>
          <p className="mt-1.5 text-28px font-semibold leading-none tabular-nums text-ink">
            <span aria-hidden="true">{puntaje}/23</span>
            <span className="sr-only">{b.label}</span>
          </p>
          <div aria-hidden="true" className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-edge">
            <div
              className="h-full origin-left rounded-full bg-clinical-attention transition-transform duration-[1100ms] ease-out motion-reduce:transition-none"
              style={{ transform: `scaleX(${vista.visto ? (b.total - 6) / 17 : 0})` }}
            />
          </div>
          <p className={`mt-1.5 text-small font-semibold text-clinical-attention transition-opacity duration-300 motion-reduce:transition-none ${clasificado ? "opacity-100" : "opacity-0"}`}>{b.clasificacion}</p>
          <p className="mt-2 text-11px text-ink-subtle dark:text-ink-muted">Seis ítems, de 6 a 23 puntos</p>
        </div>
        <div style={retraso(180)} className="pp-entra rounded-tk-md border border-edge bg-paper p-4">
          <p className={ETIQUETA}>Goteo de suero</p>
          <p className="mt-1.5 text-28px font-semibold leading-none tabular-nums text-ink">
            <span aria-hidden="true">{mlHora}</span>
            <span className="sr-only">{g.mlHora}</span>
            {" "}<span className="text-small font-medium text-ink-subtle dark:text-ink-muted">mL/h</span>
          </p>
          <p className="mt-1.5 flex items-center gap-2 text-small font-normal text-ink-subtle dark:text-ink-muted">
            <span aria-hidden="true" className="relative inline-block h-4 w-2 flex-shrink-0">
              <span className="pp-gota absolute left-0 top-0 h-2 w-2 rounded-full rounded-tl-none bg-accent" style={{ animationDuration: `${60 / g.gotasMin}s` }} />
            </span>
            {g.gotasMin} gotas/min (macro 20 gtt/mL)
          </p>
          <p className="mt-2 text-11px text-ink-subtle dark:text-ink-muted">{g.volumen.toLocaleString("es-CL")} mL en {g.horas} h</p>
        </div>
      </div>
      <div className="mt-3 rounded-tk-md border border-edge p-3.5">
        <p className={ETIQUETA}>Ficha de medicamento</p>
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {EJEMPLOS.fichaSecciones.map((s, i) => (
            <li key={s} style={retraso(380 + i * 60)} className="pp-pop rounded-full border border-edge bg-surface px-2.5 py-1 text-11px font-medium text-ink-subtle dark:text-ink-muted">{s}</li>
          ))}
        </ul>
      </div>
    </Vineta>
  );
}

// ─── Benito ──────────────────────────────────────────────────────────────────
// El panel de Benito (CompaneroGlobal) en un marco de teléfono: mismas
// burbujas (acento para ti, tinte de acento para él, con su cara), el chip de
// acción y el aviso del pie.
// En el teléfono va sin el marco de teléfono: un teléfono dibujado dentro
// del teléfono se veía desproporcionado (fundador, 29-sep). El marco vuelve
// desde sm, donde sí hay pantalla alrededor. El ancho tope lo pone quien la
// usa (`className`), así en el teléfono ocupa la misma columna que lo demás.
//
// La conversación se REPRODUCE cuando entra a la pantalla ("más acción, más
// movimiento", fundador 29-sep): la pregunta se escribe en la caja, sale,
// Benito piensa y responde palabra a palabra, como en la app (que también
// responde en streaming), y al terminar menea el trazo (estado "contento",
// el mismo de CompaneroGlobal). Se repite solo si la viñeta salió entera de
// la pantalla y volvió. Tres reglas:
//   - Sin IntersectionObserver o con reduced-motion: la conversación
//     terminada, quieta.
//   - La capa invisible con la conversación completa reserva el alto final,
//     así la página no se mueve mientras Benito escribe.
//   - El lector de pantalla lee la conversación completa una sola vez (copia
//     sr-only); la capa animada va oculta para él.
const puedeAnimar = () =>
  typeof window !== "undefined" &&
  "IntersectionObserver" in window &&
  !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const PRESENCIA_POR_FASE = {
  espera: "reposo",
  tecleando: "escuchando",
  pregunta: "escuchando",
  pensando: "pensando",
  escribiendo: "hablando",
  contento: "contento",
  listo: "reposo",
};

// Tiempos de la conversación (ms).
const MS_LETRA = 32;
const MS_PALABRA = 42;
const MS_PENSAR = 950;

function useConversacion(ref, pregunta, palabras) {
  const [animar] = useState(puedeAnimar);
  const [estado, setEstado] = useState(() => ({ fase: animar ? "espera" : "listo", letras: 0, palabras: palabras.length }));
  const [corrida, setCorrida] = useState(0);

  // Arranca al entrar a la pantalla; vuelve a arrancar solo si salió entera.
  useEffect(() => {
    if (!animar || !ref.current) return undefined;
    let salio = true;
    const obs = new IntersectionObserver(([e]) => {
      if (e.intersectionRatio >= 0.4 && salio) { salio = false; setCorrida((c) => c + 1); }
      else if (!e.isIntersecting) salio = true;
    }, { threshold: [0, 0.4] });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [animar, ref]);

  useEffect(() => {
    if (!corrida) return undefined;
    const timers = [];
    let reloj = null;
    const despues = (ms, fn) => timers.push(setTimeout(fn, ms));
    // Un paso a la vez con setInterval; al terminar llama a `luego`.
    const contar = (total, ms, poner, luego) => {
      let k = 0;
      reloj = setInterval(() => {
        k += 1;
        poner(k);
        if (k >= total) { clearInterval(reloj); reloj = null; luego(); }
      }, ms);
    };
    setEstado({ fase: "tecleando", letras: 0, palabras: 0 });
    despues(250, () => contar(pregunta.length, MS_LETRA, (k) => setEstado((e) => ({ ...e, letras: k })), () => {
      despues(350, () => {
        setEstado((e) => ({ ...e, fase: "pregunta" }));
        despues(450, () => {
          setEstado((e) => ({ ...e, fase: "pensando" }));
          despues(MS_PENSAR, () => {
            setEstado((e) => ({ ...e, fase: "escribiendo" }));
            contar(palabras.length, MS_PALABRA, (k) => setEstado((e) => ({ ...e, palabras: k })), () => {
              setEstado((e) => ({ ...e, fase: "contento" }));
              despues(2200, () => setEstado((e) => ({ ...e, fase: "listo" })));
            });
          });
        });
      });
    }));
    return () => { timers.forEach(clearTimeout); if (reloj) clearInterval(reloj); };
  }, [corrida, pregunta, palabras]);

  return { animar, ...estado };
}

function Mensajes({ b, fase, palabras, textoRespuesta }) {
  const enviada = !["espera", "tecleando"].includes(fase);
  const responde = ["escribiendo", "contento", "listo"].includes(fase);
  const termino = ["contento", "listo"].includes(fase);
  return (
    <div className="space-y-3">
      {enviada && (
        <div className="flex justify-end">
          <p className="pp-entra-derecha max-w-[85%] rounded-tk-md rounded-br-[4px] bg-accent px-3 py-2 text-small font-normal leading-relaxed text-paper">
            {b.pregunta}
          </p>
        </div>
      )}
      {fase === "pensando" && (
        <div className="pp-entra flex items-end gap-1.5">
          <span aria-hidden="true" className="mb-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-accent-border bg-accent-bg">
            <BenitoAvatar size={20} />
          </span>
          <p className="pp-puntos flex items-center gap-1 rounded-tk-md rounded-bl-[4px] border border-accent-border bg-accent-bg px-3.5 py-3 text-accent dark:text-accent-light">
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
          </p>
        </div>
      )}
      {responde && (
        <div className="flex items-end gap-1.5">
          <span aria-hidden="true" className="mb-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-accent-border bg-accent-bg">
            <BenitoAvatar size={20} />
          </span>
          <p className="min-w-0 rounded-tk-md rounded-bl-[4px] border border-accent-border bg-accent-bg px-3 py-2 text-small font-normal leading-relaxed text-ink">
            {textoRespuesta(palabras)}
            {fase === "escribiendo" && <span aria-hidden="true" className="pp-cursor ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 bg-accent" />}
          </p>
        </div>
      )}
      {termino && (
        <div className="pl-8">
          <span className="pp-pop inline-flex items-center gap-1 rounded-full border border-accent-border bg-paper px-2.5 py-1 text-micro font-semibold text-accent dark:text-accent-light">
            <Activity aria-hidden="true" size={12} strokeWidth={2} />
            {b.accion}
          </span>
        </div>
      )}
    </div>
  );
}

export function VinetaBenito({ className = "" }) {
  const b = EJEMPLOS.benito;
  const palabras = useMemo(() => b.respuesta.split(" "), [b.respuesta]);
  const textoRespuesta = (n) => (n >= palabras.length ? b.respuesta : palabras.slice(0, n).join(" "));
  const ref = useRef(null);
  const conv = useConversacion(ref, b.pregunta, palabras);
  const tecleando = conv.fase === "tecleando";
  return (
    <figure ref={ref} className={`relative mx-auto w-full rounded-tk-lg border border-edge bg-paper shadow-tk-sm sm:rounded-[34px] sm:bg-surface sm:p-2 sm:shadow-tk-modal ${className}`}>
      <figcaption className="sr-only">Ejemplo ficticio de una conversación con Benito, el compañero de MiPAE.</figcaption>
      <div className="overflow-hidden rounded-tk-lg bg-paper sm:rounded-[26px] sm:border sm:border-edge">
        <div className="flex items-center gap-2.5 border-b border-edge px-4 py-3">
          <PresenciaBenito estado={PRESENCIA_POR_FASE[conv.fase] || "reposo"} size={30} />
          <div className="min-w-0">
            <p className="text-body font-semibold leading-tight text-ink">Benito</p>
            <p className="text-11px text-ink-subtle dark:text-ink-muted">tu compañero de turno</p>
          </div>
          <span aria-hidden="true" className="ml-auto rounded-full border border-edge bg-surface px-2 py-0.5 text-micro text-ink-subtle dark:text-ink-muted">
            Ejemplo ficticio
          </span>
        </div>
        {conv.animar ? (
          <div className="grid px-3 py-4">
            <div aria-hidden="true" className="invisible col-start-1 row-start-1">
              <Mensajes b={b} fase="listo" palabras={palabras.length} textoRespuesta={textoRespuesta} />
            </div>
            <div aria-hidden="true" className="col-start-1 row-start-1">
              <Mensajes b={b} fase={conv.fase} palabras={conv.palabras} textoRespuesta={textoRespuesta} />
            </div>
            <div className="sr-only">
              <p>{b.pregunta}</p>
              <p>{b.respuesta}</p>
              <p>{b.accion}</p>
            </div>
          </div>
        ) : (
          <div className="px-3 py-4">
            <Mensajes b={b} fase="listo" palabras={palabras.length} textoRespuesta={textoRespuesta} />
          </div>
        )}
        <div className="border-t border-edge px-3 py-3">
          <div aria-hidden="true" className="flex items-center gap-2">
            {tecleando && conv.letras > 0 ? (
              <span className="flex-1 truncate rounded-full border border-accent-border px-3 py-2 text-small font-normal text-ink">
                {b.pregunta.slice(0, conv.letras)}
                <span className="pp-cursor ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 bg-accent" />
              </span>
            ) : (
              <span className="flex-1 truncate rounded-full border border-edge px-3 py-2 text-small font-normal text-ink-muted">Pídeme lo que necesites…</span>
            )}
            <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-accent-bg text-accent dark:text-accent-light">
              <Mic size={16} strokeWidth={2} />
            </span>
          </div>
          <p className="mt-2 text-11px font-normal leading-snug text-ink-subtle dark:text-ink-muted">{b.aviso}</p>
        </div>
      </div>
    </figure>
  );
}

// Viñeta por momento (lib/presentacionMiPAE, campo `vineta`).
export function VinetaMomento({ id, conSemana = false }) {
  if (id === "estudio") return <VinetaEstudio />;
  if (id === "pae") return <VinetaPae />;
  if (id === "turno") return <VinetaTurno conSemana={conSemana} />;
  if (id === "consulta") return <VinetaConsulta />;
  return null;
}
